"use client";

import { Box, Button, Container, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useMemo } from "react";
import SalifyLink from "src/lib/components/link/salify-link";
import { Carousel } from "src/lib/components/salify-next-layout/default/components/carousel";
import { COMMON_DOT_STYLES } from "src/lib/components/salify-next-layout/default/components/carousel/styles";
import { toAssetImages } from "src/lib/utils";
import {
  LayoutViewMode,
  ResponsivePropsConfig,
  resolveResponsiveProps,
  splitDesktopAndResponsive,
} from "src/lib/utils/responsive-props";

export type HeroBannerAlign = "left" | "center" | "right";

export type HeroBannerButton = {
  text: string;
  link: string;
};

export type HeroBannerItem = {
  id?: string;
  backgroundImage: string;
  heading1: string;
  heading2?: string;
  button1?: HeroBannerButton;
  button2?: HeroBannerButton;
  contentAlign?: HeroBannerAlign;
};

export type HeroBannerAnimationType = "fade" | "slide";

type HeroBannerViewportData = {
  items: HeroBannerItem[];
  minHeight?: number;
};

export interface VariantProps {
  items: HeroBannerItem[];
  /** @default 520 */
  minHeight?: number;
  /** @default true */
  animationEnabled?: boolean;
  /** @default "fade" */
  animationType?: HeroBannerAnimationType;
  /** Autoplay interval in ms. @default 5000 */
  autoplayInterval?: number;
  responsive?: ResponsivePropsConfig<HeroBannerViewportData>;
}

const ALIGN_STYLES: Record<
  HeroBannerAlign,
  { textAlign: "left" | "center" | "right"; alignItems: string; justifyContent: string }
> = {
  left: { textAlign: "left", alignItems: "flex-start", justifyContent: "flex-start" },
  center: { textAlign: "center", alignItems: "center", justifyContent: "center" },
  right: { textAlign: "right", alignItems: "flex-end", justifyContent: "flex-end" },
};

const HeroSlide = ({ item, minHeight }: { item: HeroBannerItem; minHeight: number }) => {
  const align = ALIGN_STYLES[item.contentAlign || "left"];
  const backgroundUrl =
    item.backgroundImage?.trim() !== "" ? toAssetImages(item.backgroundImage) : "";

  return (
    <Box
      sx={{
        position: "relative",
        minHeight: {
          xs: Math.round(minHeight * 0.72),
          tablet: Math.round(minHeight * 0.86),
          laptop: minHeight,
        },
        width: "100%",
        overflow: "hidden",
        bgcolor: "#0a0a0a",
        backgroundImage: backgroundUrl ? `url(${backgroundUrl})` : undefined,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(90deg, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.45) 45%, rgba(0,0,0,0.2) 100%)",
        }}
      />
      <Container
        maxWidth="lg"
        sx={{
          position: "relative",
          zIndex: 1,
          height: "100%",
          minHeight: "inherit",
          display: "flex",
          alignItems: "center",
          py: { xs: 4, laptop: 6 },
        }}
      >
        <Box
          sx={{
            maxWidth: { xs: "100%", laptop: 640 },
            display: "flex",
            flexDirection: "column",
            gap: 2,
            color: "#fff",
            ...align,
          }}
        >
          {item.heading1 ? (
            <Typography
              component="h1"
              sx={{
                fontWeight: 700,
                fontSize: { xs: 28, tablet: 34, laptop: 42 },
                lineHeight: 1.15,
                letterSpacing: "-0.02em",
              }}
            >
              {item.heading1}
            </Typography>
          ) : null}

          {item.heading2 ? (
            <Typography
              component="p"
              sx={{
                fontSize: { xs: 14, tablet: 15, laptop: 16 },
                lineHeight: 1.7,
                color: "rgba(255,255,255,0.9)",
                whiteSpace: "pre-line",
              }}
            >
              {item.heading2}
            </Typography>
          ) : null}

          {(item.button1?.text || item.button2?.text) && (
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1.5,
                mt: 0.5,
                justifyContent: align.justifyContent,
              }}
            >
              {item.button1?.text ? (
                <SalifyLink href={item.button1.link || "#"}>
                  <Button
                    variant="contained"
                    sx={{
                      bgcolor: "#FFC107",
                      color: "#111",
                      fontWeight: 600,
                      px: 3,
                      py: 1.25,
                      borderRadius: 1,
                      boxShadow: "none",
                      "&:hover": { bgcolor: "#e6ad06", boxShadow: "none" },
                    }}
                  >
                    {item.button1.text}
                  </Button>
                </SalifyLink>
              ) : null}
              {item.button2?.text ? (
                <SalifyLink href={item.button2.link || "#"}>
                  <Button
                    variant="outlined"
                    sx={{
                      color: "#fff",
                      borderColor: "rgba(255,255,255,0.6)",
                      fontWeight: 600,
                      px: 3,
                      py: 1.25,
                      borderRadius: 1,
                      "&:hover": {
                        borderColor: "#fff",
                        bgcolor: "rgba(255,255,255,0.08)",
                      },
                    }}
                  >
                    {item.button2.text}
                  </Button>
                </SalifyLink>
              ) : null}
            </Box>
          )}
        </Box>
      </Container>
    </Box>
  );
};

const useLayoutViewMode = (): LayoutViewMode => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("tablet"));
  const isTablet = useMediaQuery(theme.breakpoints.between("tablet", "laptop"));
  if (isMobile) return "mobile";
  if (isTablet) return "tablet";
  return "desktop";
};

const HeroBannerDefaultV2 = (props: VariantProps) => {
  const viewMode = useLayoutViewMode();
  const { desktop, responsive } = splitDesktopAndResponsive(
    props as unknown as Record<string, unknown>,
  );

  const resolved = useMemo(
    () =>
      resolveResponsiveProps(
        {
          items: (desktop.items as HeroBannerItem[]) || [],
          minHeight: (desktop.minHeight as number) ?? 520,
        },
        responsive,
        viewMode,
        ["items", "minHeight"],
      ),
    [desktop.items, desktop.minHeight, responsive, viewMode],
  );

  const items: HeroBannerItem[] =
    Array.isArray(resolved.items) && resolved.items.length ? resolved.items : [];
  const minHeight = typeof resolved.minHeight === "number" ? resolved.minHeight : 520;
  const animationEnabled = props.animationEnabled !== false;
  const animationType = props.animationType || "fade";
  const autoplayInterval = props.autoplayInterval ?? 5000;
  const shouldAnimate = animationEnabled && items.length > 1;

  if (!items.length) {
    return null;
  }

  const slides = items.map((item, index) => (
    <HeroSlide key={item.id || `hero-slide-${index}`} item={item} minHeight={minHeight} />
  ));

  if (!shouldAnimate) {
    return <Box component="section">{slides[0]}</Box>;
  }

  return (
    <Box component="section" id="hero-banner">
      <Carousel
        dots
        arrows={false}
        arrowStyles={undefined}
        slidesToShow={1}
        slidesToScroll={1}
        infinite
        autoplay
        autoplaySpeed={autoplayInterval}
        speed={animationType === "fade" ? 900 : 600}
        fade={animationType === "fade"}
        dotStyles={COMMON_DOT_STYLES as any}
        dotColor="#FFC107"
      >
        {slides}
      </Carousel>
    </Box>
  );
};

export default HeroBannerDefaultV2;
